import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  // Visibility as ViewIcon,
  // People as PeopleIcon,
} from '@mui/icons-material';
import StatsWidget from '../components/dashboard/StatsWidget';
import RecentDocumentsWidget from '../components/dashboard/RecentDocumentsWidget';
// import SearchTrendsWidget from '../components/dashboard/SearchTrendsWidget';
import AnnouncementsWidget from '../components/dashboard/AnnouncementsWidget';
// import IntegrationNotice from '../components/common/IntegrationNotice';
import { documentService } from '../services/document.service';
import { formatDocumentTitle } from '../utils/documentUtils';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);

  // Mock data - replace with actual API calls
  const [stats, setStats] = useState({
    totalDocuments: 0,
    recentUploads: 0,
    totalViews: 0,
    activeUsers: 0,
  });

  const [recentDocuments, setRecentDocuments] = useState<any[]>([]);
  // const [searchTrends, setSearchTrends] = useState<any[]>([]);
  const announcements = useMemo(() => [
    {
      id: 'drbfm-knowledge-references',
      title: t('dashboard.announcementItems.drbfmKnowledge.title'),
      message: t('dashboard.announcementItems.drbfmKnowledge.message'),
      type: 'success' as const,
      author: t('dashboard.announcementAuthor'),
      createdAt: new Date().toISOString(),
      priority: 'high' as const,
    },
    {
      id: 'chat-rag-quality',
      title: t('dashboard.announcementItems.chatQuality.title'),
      message: t('dashboard.announcementItems.chatQuality.message'),
      type: 'success' as const,
      author: t('dashboard.announcementAuthor'),
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      priority: 'high' as const,
    },
    {
      id: 'audit-operations',
      title: t('dashboard.announcementItems.auditOperations.title'),
      message: t('dashboard.announcementItems.auditOperations.message'),
      type: 'success' as const,
      author: t('dashboard.announcementAuthor'),
      createdAt: new Date(Date.now() - 86400000 * 2).toISOString(),
      priority: 'medium' as const,
    },
    {
      id: 'help-refresh',
      title: t('dashboard.announcementItems.helpRefresh.title'),
      message: t('dashboard.announcementItems.helpRefresh.message'),
      type: 'info' as const,
      author: t('dashboard.announcementAuthor'),
      createdAt: new Date(Date.now() - 86400000 * 3).toISOString(),
      priority: 'low' as const,
    },
  ], [t]);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Fetch all documents
        const response = await documentService.getDocuments({
          page: 1,
          pageSize: 100, // Get more documents for stats calculation
          sortBy: 'uploadedAt',
          sortOrder: 'desc'
        });

        if (response.results && response.results.length > 0) {
          // Calculate stats from real data
          const totalDocuments = response.total;

          // Calculate recent uploads (last 7 days)
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          const recentUploads = response.results.filter((doc: any) => {
            const uploadDate = new Date(doc.uploadedAt || doc.createdAt);
            return uploadDate >= sevenDaysAgo;
          }).length;

          setStats({
            totalDocuments: totalDocuments,
            recentUploads: recentUploads,
            totalViews: totalDocuments * 3, // Mock views for now
            activeUsers: 2, // Mock users for now
          });

          // Get the most recent 5 documents for the recent documents widget
          const recentDocs = response.results.slice(0, 5).map((doc: any) => ({
            id: doc.id,
            title: formatDocumentTitle(doc.title, doc.fileName),
            fileName: doc.fileName,
            category: doc.category || 'General',
            uploadedBy: doc.createdByName || doc.createdBy || doc.uploadedBy || 'Unknown',
            uploadedAt: doc.uploadedAt || doc.createdAt,
            fileType: doc.mimeType?.split('/').pop() || 'unknown',
            size: doc.fileSize || 0,
          }));

          setRecentDocuments(recentDocs);
        } else {
          // No documents found, set default values
          setStats({
            totalDocuments: 0,
            recentUploads: 0,
            totalViews: 0,
            activeUsers: 0,
          });
          setRecentDocuments([]);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
        // Fallback to some default values
        setStats({
          totalDocuments: 0,
          recentUploads: 0,
          totalViews: 0,
          activeUsers: 0,
        });
        setRecentDocuments([]);
      }
      setLoading(false);
    };

    loadDashboardData();
  }, []);

  const handleDocumentClick = (documentId: string) => {
    navigate(`/documents/${documentId}`);
  };

  // const handleSearchTermClick = (term: string) => {
  //   navigate(`/search?q=${encodeURIComponent(term)}`);
  // };

  // Announcement click removed — announcements are display-only

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.title')}
      </Typography>

      {/* Integration Status Notice */}
      {/* <IntegrationNotice /> */}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsWidget
            title={t('dashboard.totalDocuments')}
            value={stats.totalDocuments}
            icon={<DocumentIcon />}
            color="primary"
            // trend={stats.totalDocuments > 0 ? { value: Math.min(100, Math.round((stats.recentUploads / stats.totalDocuments) * 100)), direction: 'up' } : undefined}
            // subValue={t('dashboard.fromLastMonth')}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsWidget
            title={t('dashboard.recentUploads')}
            value={stats.recentUploads}
            icon={<UploadIcon />}
            color="secondary"
            subValue={t('dashboard.inLast7Days')}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <RecentDocumentsWidget
            documents={recentDocuments}
            loading={loading}
            onDocumentClick={handleDocumentClick}
            onViewAll={() => navigate('/documents')}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Grid container spacing={3}>
            {/* <Grid size={12}>
              <SearchTrendsWidget
                searchTerms={searchTrends}
                loading={loading}
                onTermClick={handleSearchTermClick}
              />
            </Grid> */}
            <Grid size={12}>
              <AnnouncementsWidget
                announcements={announcements}
                loading={loading}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
